// src/views/admin/users/customers/view/tabs/CreditTab.jsx

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Card from '@/components/ui/cards/Card';
import ToastMe from '@/components/ui/ToastMe';
import { getCreditReportService } from '@/services/CustomerServices';

export default function CreditTab({ customerId }) {
  const dispatch = useDispatch();
  const [report, setReport] = useState({ summary: null, ledger: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(getCreditReportService({ customerId }))
      .then(res => {
        const data = res.data?.data || {};
        setReport({
          summary: data.summary || null,
          ledger: data.ledger || [],
        });
      })
      .catch(err => {
        ToastMe(err.message || 'Failed to load credit report', 'error');
      })
      .finally(() => setLoading(false));
  }, [customerId, dispatch]);

  const { summary, ledger } = report;

  if (loading) {
    return <Card className="p-6 text-center">Loading…</Card>;
  }

  if (!summary) {
    return <Card className="p-6 text-center">No credit history available.</Card>;
  }

  return (
    <>
      {/* Summary */}
      <Card className="mb-6 p-4 bg-gray-50">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-medium">Total Purchases</div>
            <div>{summary.purchaseCount} orders × ${summary.totalPurchases.toFixed(2)}</div>
          </div>
          <div>
            <div className="font-medium">Total Repayments</div>
            <div>{summary.repaymentCount} payments × ${summary.totalRepayments.toFixed(2)}</div>
          </div>
          <div>
            <div className="font-medium">Current Balance</div>
            <div className="text-lg font-semibold">${summary.currentBalance.toFixed(2)}</div>
          </div>
        </div>
      </Card>

      {/* Ledger Table */}
      <Card className="overflow-x-auto">
        <table className="min-w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-slate-100 text-left">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Debit</th>
              <th className="px-4 py-3 text-right">Credit</th>
              <th className="px-4 py-3 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="px-4 py-2">{new Date(r.date).toLocaleString()}</td>
                <td className="px-4 py-2">{r.description}</td>
                <td className="px-4 py-2 text-right">${r.debit.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">${r.credit.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">${r.runningBalance.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

CreditTab.propTypes = {
  customerId: PropTypes.string.isRequired,
};
